import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

import { EventsService, Event } from './events.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isSameDay, format } from 'date-fns';
import { EventDetailsDialogComponent } from './event-details-dialog.component';
import { EventFormDialogComponent } from './event-form-dialog.component';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import { AuthService } from '../shared/auth/auth.service';
import { EventCardComponent } from './event-card.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDividerModule } from '@angular/material/divider';
import { ActivatedRoute } from '@angular/router';
import { NavbarComponent } from '../topNavBar/navbar.component';
import { FooterComponent } from '../shared/footer/footer.component';

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [
  EventCardComponent,
  CommonModule,
      ReactiveFormsModule,
      HttpClientModule,
      // Material Modules
      MatButtonModule,
      MatCardModule,
      MatDialogModule,
        RouterModule,
      MatIconModule,
      MatProgressSpinnerModule,
      MatSnackBarModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      MatDatepickerModule,
          MatDividerModule,
      MatNativeDateModule,
      NavbarComponent,
      FooterComponent
  ],
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss']
})
export class EventsComponent implements OnInit, OnDestroy {


  private destroy$ = new Subject<void>();

  events: Event[] = [];
  upcomingEvents: Event[] = [];
  pastEvents: Event[] = [];

  isLoading = false;
  isError = false;
  errorMessage = '';

  isMenuOpen = false;
  isAuthenticated = false;
  isSystemAdmin = false;
  isSuperAdmin = false;
  userRoles: string[] = [];
  currentUserOrgId: string | undefined;

  constructor(
    private eventsService: EventsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private route: ActivatedRoute,
     iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer
  ) {
    iconRegistry.addSvgIcon(
      'facebook',
      sanitizer.bypassSecurityTrustResourceUrl('/facebook-svgrepo-com.svg')
    );
    iconRegistry.addSvgIcon(
      'twitter',
      sanitizer.bypassSecurityTrustResourceUrl('/twitter-svgrepo-com.svg')
    );
    iconRegistry.addSvgIcon(
      'linkedin',
      sanitizer.bypassSecurityTrustResourceUrl('/linkedin-linked-in-svgrepo-com.svg')
    );
  }
  currentYear = new Date().getFullYear();

  ngOnInit(): void {
    this.checkAuthStatus();
    this.loadEvents();
this.route.queryParams
    .pipe(takeUntil(this.destroy$))
    .subscribe(params => {
      if (params['eventId']) {
        this.openEventById(params['eventId']);
      }
    });
    console.log('eventsss',this.upcomingEvents)
    this.eventsService.eventsChanged$
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      this.loadEvents();
    });
  }


  private openEventById(eventId: string): void {
  // Wait for events to load first, then open the specific one
  const tryOpen = () => {
    const found = this.events.find(e => e.id === eventId);
    if (found) {
      this.openEventDetails(found);
    } else if (this.isLoading) {
      // Events still loading, retry after short delay
      setTimeout(tryOpen, 300);
    }
  };
  tryOpen();
}
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkAuthStatus(): void {
    // Check if user is authenticated
    this.isAuthenticated = this.authService.isAuthenticated();

    if (this.isAuthenticated) {
      const currentUser = this.authService.getCurrentUser();

      if (currentUser) {
        this.userRoles = currentUser.roles || [];
        this.currentUserOrgId = currentUser.organizationId || currentUser.hospitalId;

        // Check if user is superadmin or admin
        this.isSuperAdmin = currentUser.isSuperAdmin || false;
        this.isSystemAdmin = this.userRoles.includes('admin') ||
                            this.userRoles.includes('superadmin') ||
                            this.isSuperAdmin;
      }
    }
  }

loadEvents(): void {
  this.isLoading = true;
  this.isError = false;
  this.errorMessage = '';

  // Use the new visible events endpoint
  this.eventsService.getVisibleEvents()
    .subscribe({
      next: (response) => {
        this.events = response.data;  // Extract the data property
        this.sortAndFilterEvents();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading events:', error);
        this.isError = true;
        this.errorMessage = error.message || 'Failed to load events';
        this.isLoading = false;
      }
    });
}
  sortAndFilterEvents(): void {
    const currentDate = new Date();

    const sorted = [...this.events].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    this.upcomingEvents = sorted.filter(
      event => new Date(event.end) >= currentDate
    );

    this.pastEvents = sorted
      .filter(event => new Date(event.end) < currentDate)
      .reverse();
  }

  /**
   * Check if user can edit/delete a specific event
   * SuperAdmin can modify all events
   * Admin can only modify events from their organization
   */
  canModifyEvent(event: Event): boolean {
    if (!this.isAuthenticated || !this.isSystemAdmin) {
      return false;
    }

    // SuperAdmin can modify all events
    if (this.isSuperAdmin) {
      return true;
    }

    // Regular admin can only modify events from their organization
    if (this.isSystemAdmin && event.organizationId) {
      return event.organizationId === this.currentUserOrgId;
    }

    return false;
  }

  openEventDetails(event: Event): void {
    const canModify = this.canModifyEvent(event);

    const dialogRef = this.dialog.open(EventDetailsDialogComponent, {
      width: '95vw',
      maxWidth: '1536px',
      maxHeight: '90vh',
      data: {
        event,
        canModify,
        isAuthenticated: this.isAuthenticated,
        isSuperAdmin: this.isSuperAdmin
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'edit' && canModify) {
        this.openEditDialog(result.event);
      } else if (result?.action === 'delete' && canModify) {
        this.confirmDelete(result.event);
      }
    });
  }

openAddDialog(): void {
  if (!this.isAuthenticated) {
    this.showSnackbar('Please log in to create events', 'error');
    return;
  }
  if (!this.isSystemAdmin) {
    this.showSnackbar('Only admins can create events', 'error');
    return;
  }

  const openDialog = (organizations: { id: string; name: string }[] = []) => {
    const dialogRef = this.dialog.open(EventFormDialogComponent, {
      width: '90%',
      maxWidth: '1200px',
      maxHeight: '90vh',
      data: {
        mode: 'create',
        organizationId: this.currentUserOrgId,
        organizations
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.createEvent(result.data);
      }
    });
  };

  if (this.isSuperAdmin) {
    this.eventsService.getOrganizationsLightweight()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orgs) => openDialog(orgs),
        error: (err) => {
          console.error('Failed to load organizations:', err);
          openDialog(); // still open the dialog, just without the org selector populated
        }
      });
  } else {
    openDialog();
  }
}

openEditDialog(event: Event): void {
  if (!this.canModifyEvent(event)) {
    this.showSnackbar('You do not have permission to edit this event', 'error');
    return;
  }

  const dialogRef = this.dialog.open(EventFormDialogComponent, {
    width: '90%',           // Changed from 900px
    maxWidth: '1200px',     // Add max width
    maxHeight: '90vh',
    data: {
      mode: 'edit',
      event: event,
      organizationId: this.currentUserOrgId
    }
  });

  dialogRef.afterClosed().subscribe(result => {
    if (result?.success) {
      this.updateEvent(event.id, result.data);
    }
  });
}

  createEvent(eventData: Partial<Event>): void {
    // Add organization ID to the event data
    const dataWithOrg = {
      ...eventData,
      organizationId: this.currentUserOrgId
    };

    this.eventsService.createEvent(dataWithOrg)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSnackbar('Event created successfully', 'success');
          //this.loadEvents();
        },
        error: (error) => {
          this.showSnackbar('Failed to create event', 'error');
        }
      });
  }

  updateEvent(id: string, eventData: Partial<Event>): void {
    console.log('evenupdtae data' , eventData)
    this.eventsService.updateEvent(id, eventData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSnackbar('Event updated successfully', 'success');
          //this.loadEvents();
        },
        error: (error) => {
          this.showSnackbar('Failed to update event', 'error');
        }
      });
  }

  confirmDelete(event: Event): void {
    if (!this.canModifyEvent(event)) {
      this.showSnackbar('You do not have permission to delete this event', 'error');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Event',
        message: `Are you sure you want to delete "${event.title}"? This action cannot be undone.`
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.deleteEvent(event.id);
      }
    });
  }

  deleteEvent(id: string): void {
    this.eventsService.deleteEvent(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSnackbar('Event deleted successfully', 'success');
          //this.loadEvents();
        },
        error: (error) => {
          this.showSnackbar('Failed to delete event', 'error');
        }
      });
  }

  showSnackbar(message: string, type: 'success' | 'error'): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: type === 'success' ? 'snackbar-success' : 'snackbar-error'
    });
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }
}
